// src/components/providerAssignments/AssignmentHistoryDrawer.tsx
//
// Chronological view of an assignment's history (spec §11). Each row
// shows the action label, actor, timestamp, and a human-readable
// before/after summary where the changeDiff carries one — never a raw
// JSON dump.

import { useEffect } from 'react';
import { X } from 'lucide-react';
import { useGetProviderAssignmentHistoryQuery } from '../../api/providerAssignmentsApi';
import type {
  AssignmentHistoryAction,
  PersonProviderAssignment,
  PersonProviderAssignmentHistory,
} from '../../types/api';
import {
  HISTORY_ACTION_LABELS,
  formatDateTime,
  providerLabel,
} from './assignmentDisplay';

export interface AssignmentHistoryDrawerProps {
  assignment: PersonProviderAssignment;
  onClose: () => void;
}

export default function AssignmentHistoryDrawer({
  assignment,
  onClose,
}: AssignmentHistoryDrawerProps) {
  const { data: history = [], isLoading } = useGetProviderAssignmentHistoryQuery(
    assignment.id,
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-end bg-slate-900/40"
      role="dialog"
      aria-modal="true"
      aria-labelledby="history-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      data-testid="assignment-history-drawer"
    >
      <aside className="h-full w-full max-w-md bg-white shadow-xl ring-1 ring-slate-200 flex flex-col">
        <div className="flex items-center gap-3 px-5 py-3 border-b border-slate-200">
          <h2 id="history-title" className="flex-1 text-sm font-semibold text-slate-900">
            Assignment history
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-3">
          {isLoading && <p className="text-xs text-slate-500">Loading history…</p>}
          {!isLoading && history.length === 0 && (
            <p className="text-xs text-slate-500">No history yet.</p>
          )}
          {!isLoading && history.length > 0 && (
            <ol className="space-y-2">
              {history.map((row) => (
                <HistoryEntry key={row.id} row={row} />
              ))}
            </ol>
          )}
        </div>
      </aside>
    </div>
  );
}

function HistoryEntry({ row }: { row: PersonProviderAssignmentHistory }) {
  const actionLabel = HISTORY_ACTION_LABELS[row.action as AssignmentHistoryAction] ?? row.action;
  const summary = renderChangeSummary(row);
  return (
    <li
      className="rounded-md border border-slate-200 bg-white p-3"
      data-testid={`history-row-${row.action}`}
    >
      <div className="flex items-center justify-between gap-2 text-xs">
        <span className="font-medium text-slate-900">{actionLabel}</span>
        <time className="text-slate-500">{formatDateTime(row.createdAt)}</time>
      </div>
      <div className="mt-0.5 text-[11px] text-slate-500">
        by {providerLabel(row.actor) || 'system'}
      </div>
      {summary && (
        <div className="mt-2 space-y-0.5 text-[11px] text-slate-600">{summary}</div>
      )}
      {row.note && (
        <p className="mt-2 rounded bg-slate-50 px-2 py-1 text-[11px] text-slate-600">
          &ldquo;{row.note}&rdquo;
        </p>
      )}
    </li>
  );
}

/**
 * Human-friendly projection of `changeDiff`. Handles the shapes the
 * service actually emits:
 *   { after: { ...snapshot } }                        → created
 *   { before, after: { status, endDate, ... } }       → ended
 *   { fields: { <field>: { before, after } } }        → generic per-field
 *   { before, after, meta: { successorProviderUserId, taskTransferDeferred } }
 *                                                     → reassigned
 * Anything else is silently omitted — we never dump raw JSON in normal UX.
 */
function renderChangeSummary(row: PersonProviderAssignmentHistory): React.ReactNode {
  const diff = row.changeDiff as any;
  if (!diff || typeof diff !== 'object') return null;

  if (diff.fields && typeof diff.fields === 'object') {
    return Object.entries(diff.fields).map(([field, delta]) => {
      const d = delta as { before?: unknown; after?: unknown };
      return (
        <div key={field}>
          <span className="text-slate-500">{fieldLabel(field)}: </span>
          <span className="line-through">{formatValue(d.before)}</span>
          <span> → </span>
          <span className="font-medium text-slate-800">{formatValue(d.after)}</span>
        </div>
      );
    });
  }

  if (row.action === 'reassigned' && diff.after) {
    const meta = diff.meta as { taskTransferDeferred?: boolean } | undefined;
    return (
      <>
        {diff.before?.providerUserId && diff.after?.successorProviderUserId && (
          <div>
            <span className="text-slate-500">Provider: </span>
            <span className="line-through">{diff.before.providerUserId}</span>
            <span> → </span>
            <span className="font-medium text-slate-800">
              {diff.after.successorProviderUserId}
            </span>
          </div>
        )}
        {meta?.taskTransferDeferred && (
          <div className="text-amber-700">
            Task transfer was requested but not automated.
          </div>
        )}
      </>
    );
  }

  if (row.action === 'ended' && diff.after) {
    return (
      <div>
        Status: <span className="line-through">{formatValue(diff.before?.status)}</span>{' '}
        → <span className="font-medium text-slate-800">{formatValue(diff.after?.status)}</span>
      </div>
    );
  }

  return null;
}

function fieldLabel(field: string): string {
  switch (field) {
    case 'providerUserId':
      return 'Provider';
    case 'providerRoleId':
      return 'Role';
    case 'departmentCircleId':
      return 'Department';
    case 'priority':
      return 'Priority';
    case 'status':
      return 'Status';
    case 'dates':
      return 'Dates';
    default:
      return field;
  }
}

function formatValue(v: unknown): string {
  if (v == null) return '—';
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (v instanceof Date) return v.toLocaleDateString();
  if (typeof v === 'object') {
    // Dates-object shape.
    const o = v as { startDate?: string | Date; endDate?: string | Date | null };
    if (o.startDate !== undefined || o.endDate !== undefined) {
      const s = o.startDate ? new Date(o.startDate as any).toLocaleDateString() : '—';
      const e = o.endDate ? new Date(o.endDate as any).toLocaleDateString() : '—';
      return `${s} → ${e}`;
    }
  }
  return '—';
}
